import { RichText } from './RichText';
import { MapsWidget } from './MapsWidget';

const card = 'bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]';
const h2 = 'font-display text-xl text-warm-dark mb-4 tracking-wide';

const PRECO_LABEL: Record<string, string> = {
  $: '$ · econômico',
  $$: '$$ · intermediário',
  $$$: '$$$ · alto padrão',
};

/** Barra de prova social consolidada (TripAdvisor + Google + Booking) */
export function RestauranteProvaSocial({ entity: e }: { entity: any }) {
  const r = e.rating || {};
  const rc = e.reviewCount || {};
  const items = [
    r.tripAdvisor && { fonte: 'TripAdvisor', nota: Number(r.tripAdvisor).toFixed(1), max: '5', count: rc.tripAdvisor },
    r.google && { fonte: 'Google', nota: Number(r.google).toFixed(1), max: '5', count: rc.google },
    r.booking && { fonte: 'Booking', nota: Number(r.booking).toFixed(1), max: '10', count: rc.booking },
  ].filter(Boolean) as { fonte: string; nota: string; max: string; count?: number }[];
  if (!items.length) return null;
  return (
    <div className={card}>
      <h2 className={h2}>Avaliações em todas as plataformas</h2>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {items.map(it => (
          <div key={it.fonte} className="text-center bg-sand-50 rounded-xl py-4 px-2 border border-sand-100">
            <div className="font-display text-3xl text-teal-deep leading-none">{it.nota}</div>
            <div className="font-body text-[11px] text-warm-gray mt-1">/{it.max} · {it.fonte}</div>
            {it.count ? <div className="font-body text-[11px] text-warm-gray mt-0.5">{it.count} avaliações</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Ficha rápida — vila, cozinha, preço, horário, reserva, pagamento (gap nº1: ninguém publica horário/preço) */
function FichaRapida({ entity: e }: { entity: any }) {
  const rows = [
    ['📍', 'Vila', e.vilarejoName],
    ['🍽️', 'Cozinha', (e.cuisine || []).join(' · ') || null],
    ['💰', 'Faixa de preço', e.faixaPreco ? (PRECO_LABEL[e.faixaPreco] || e.faixaPreco) : null],
    ['🕐', 'Horário', e.horarios],
    ['📅', 'Reserva', e.reservaRecomendada === true ? 'Recomendada (alta temporada)' : e.reservaRecomendada === false ? 'Sem necessidade' : null],
    ['💳', 'Pagamento', e.formasPagamento],
  ].filter(r => r[2]);
  if (!rows.length && !e.ambiente) return null;
  return (
    <div className={card} id="ficha">
      <h2 className={h2}>Ficha rápida</h2>
      {rows.length > 0 && (
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {rows.map(([icon, k, v]) => (
            <div key={k as string} className="flex gap-3">
              <span className="text-xl flex-shrink-0">{icon}</span>
              <div>
                <dt className="font-body text-[11px] text-warm-gray uppercase tracking-wide">{k}</dt>
                <dd className="font-body text-sm text-warm-dark font-medium">{v as string}</dd>
              </div>
            </div>
          ))}
        </dl>
      )}
      {e.ambiente && (
        <div className="mt-5 pt-5 border-t border-sand-100">
          <p className="font-body font-semibold text-warm-dark text-sm mb-1">Ambiente</p>
          <p className="font-body text-sm text-warm-gray leading-relaxed">{e.ambiente}</p>
        </div>
      )}
    </div>
  );
}

/** Cardápio — pratos de assinatura + destaques (gap: concorrente não detalha cardápio) */
function Cardapio({ entity: e }: { entity: any }) {
  const destaques = (e.cardapioDestaques || []).filter((d: any) => d.nome);
  const assinatura = e.pratosAssinatura || [];
  if (!destaques.length && !assinatura.length) return null;
  return (
    <div className="bg-gradient-to-br from-coral/5 to-amber-50 rounded-2xl border border-coral/20 p-6" id="cardapio">
      <h2 className={h2}>🍽️ O que pedir</h2>
      {assinatura.length > 0 && (
        <div className="mb-5">
          <p className="font-body font-semibold text-warm-dark text-sm mb-2">Pratos da casa</p>
          <div className="flex flex-wrap gap-2">
            {assinatura.map((p: string, i: number) => (
              <span key={i} className="font-body text-xs bg-white/70 border border-coral/20 text-warm-dark px-3 py-1 rounded-full">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}
      {destaques.length > 0 && (
        <ul className="space-y-3">
          {destaques.map((d: any, i: number) => (
            <li key={i} className="flex gap-3">
              <span className="text-teal-deep mt-1 flex-shrink-0">•</span>
              <div>
                <p className="font-body font-semibold text-warm-dark text-sm">{d.nome}</p>
                {d.descricao && <p className="font-body text-sm text-warm-gray leading-snug">{d.descricao}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Como chegar + aviso do trapiche correto (gap nº1) */
function ComoChegar({ entity: e }: { entity: any }) {
  const geo = e.schemaOrg?.geo;
  if (!e.comoChegar && !e.proximidades?.length && !geo) return null;
  return (
    <div className={card} id="como-chegar">
      <h2 className={h2}>Como chegar</h2>
      {e.comoChegar && <RichText text={e.comoChegar} />}
      {e.trapicheAviso && (
        <div className="mt-4 flex gap-3 p-4 bg-coral/5 border border-coral/20 rounded-xl">
          <span className="text-xl flex-shrink-0">⚠️</span>
          <div className="font-body text-sm text-warm-dark leading-relaxed">
            <RichText text={e.trapicheAviso} />
          </div>
        </div>
      )}
      {geo?.latitude && geo?.longitude && (
        <div className="mt-4 rounded-xl overflow-hidden border border-sand-200">
          <MapsWidget lat={geo.latitude} lng={geo.longitude} title={e.title} height={260} />
        </div>
      )}
      {e.proximidades?.length > 0 && (
        <div className="mt-5">
          <p className="font-body font-semibold text-warm-dark text-sm mb-2">O que tem por perto</p>
          <ul className="space-y-1.5">
            {e.proximidades.map((p: string, i: number) => (
              <li key={i} className="flex items-start gap-2 font-body text-sm text-warm-gray">
                <span className="text-teal-deep mt-1 flex-shrink-0">📍</span> {p}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Avaliações reais (citações com autor + fonte) */
function Avaliacoes({ entity: e }: { entity: any }) {
  const reviews = (e.reviews || []).filter((r: any) => r.text && r.author);
  if (!reviews.length) return null;
  const srcLabel: Record<string, string> = { booking: 'Booking', tripadvisor: 'TripAdvisor', google: 'Google' };
  return (
    <div className={card} id="avaliacoes">
      <h2 className={h2}>O que dizem quem comeu lá</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {reviews.map((r: any, i: number) => (
          <figure key={i} className="bg-sand-50 rounded-xl p-4 border border-sand-100">
            <div className="text-amber-500 text-sm mb-1">{'★'.repeat(Math.round(r.rating > 5 ? r.rating / 2 : r.rating))}</div>
            <blockquote className="font-body text-sm text-warm-dark leading-relaxed">“{r.text}”</blockquote>
            <figcaption className="font-body text-xs text-warm-gray mt-2">
              {r.author} · {srcLabel[r.source] || r.source}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

/** FAQ visível (accordion) — necessário para rich results FAQPage */
function FAQ({ entity: e }: { entity: any }) {
  const faq = (e.faq || []).filter((f: any) => f.pergunta && f.resposta);
  if (!faq.length) return null;
  return (
    <div className={card} id="faq">
      <h2 className={h2}>Perguntas frequentes</h2>
      <div className="space-y-3">
        {faq.map((f: any, i: number) => (
          <details key={i} className="group bg-sand-50 border border-sand-200 rounded-lg overflow-hidden">
            <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-100 list-none">
              <span className="font-display text-base text-warm-dark pr-4">{f.pergunta}</span>
              <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
            </summary>
            <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{f.resposta}</div>
          </details>
        ))}
      </div>
    </div>
  );
}

/** Conjunto de seções da super página de restaurante (renderiza só o que a entidade tiver) */
export function RestauranteSuperSections({ entity }: { entity: any }) {
  return (
    <>
      <FichaRapida entity={entity} />
      <Cardapio entity={entity} />
      <ComoChegar entity={entity} />
      <Avaliacoes entity={entity} />
      <FAQ entity={entity} />
    </>
  );
}
